import React, { useState } from 'react';
import axios from 'axios';
import { APP_API_URL } from '../../utils';
import Swal from 'sweetalert2';
import formatNumber, { formatDateWithTime } from '../../utils/functions';
import { useSelector } from 'react-redux';
import DuoModal from '../../components/ui/duo-modal';

const DetailRow = ({ label, value }) => {
  if (value == null || value === '') return null;
  return (
    <div className="duo-detail-row">
      <span className="duo-detail-row__label">{label}</span>
      <span className="duo-detail-row__value">{value}</span>
    </div>
  );
};

const statusBadgeClass = (status) => {
  if (status === 'terminé' || status === 'reçu') return 'duo-badge-pill duo-badge-pill--ok';
  if (status === 'en cours') return 'duo-badge-pill duo-badge-pill--info';
  if (status === 'en attente') return 'duo-badge-pill duo-badge-pill--warn';
  if (status === 'annulé') return 'duo-badge-pill duo-badge-pill--off';
  return 'duo-badge-pill';
};

const statusLabel = (status) => {
  if (status === 'en cours') return 'En cours';
  if (status === 'terminé') return 'Terminé';
  if (status === 'annulé') return 'Annulé';
  if (status === 'en attente') return 'En attente';
  if (status === 'reçu') return 'Reçu';
  return status;
};

export default function ConfirmReception({ provision, refresh }) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;
  const isTermine = provision.status === 'terminé';

  const closeModal = () => setIsOpen(false);

  const handleConfirmReception = async () => {
    if (isTermine) {
      Swal.fire({
        icon: 'info',
        title: 'Action impossible',
        text: 'Cet approvisionnement est déjà terminé. Vous ne pouvez pas confirmer la réception.',
      });
      return;
    }

    Swal.fire({
      title: 'Confirmer la réception ?',
      text: 'Voulez-vous confirmer la réception de cet approvisionnement ?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Oui, confirmer',
      cancelButtonText: 'Annuler',
      reverseButtons: true,
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.put(
          `${APP_API_URL}/approvisionnement/${provision.id}/completer`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.status === 200) {
          Swal.fire({
            icon: 'success',
            title: 'Réception confirmée',
            text: 'La réception de l\'approvisionnement a été confirmée avec succès.',
            confirmButtonText: 'OK',
          }).then(() => {
            closeModal();
            refresh();
          });
        }
      } catch (error) {
        console.error('Erreur lors de la confirmation de la réception', error);
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Une erreur est survenue lors de la confirmation. Veuillez réessayer.',
        });
      } finally {
        setIsLoading(false);
      }
    });
  };

  const handleDeleteProvision = async () => {
    Swal.fire({
      title: 'Supprimer cet approvisionnement ?',
      text: 'Cette action est irréversible. Voulez-vous continuer ?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
      reverseButtons: true,
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const response = await axios.put(
          `${APP_API_URL}/approvisionnement/${provision.id}/delete`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (response.status === 200) {
          Swal.fire({
            icon: 'success',
            title: 'Suppression réussie',
            text: 'L\'approvisionnement a été supprimé avec succès.',
            confirmButtonText: 'OK',
          }).then(() => {
            closeModal();
            refresh();
          });
        }
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'approvisionnement', error);
        Swal.fire({
          icon: 'error',
          title: 'Erreur',
          text: 'Une erreur est survenue lors de la suppression. Veuillez réessayer.',
        });
      } finally {
        setIsLoading(false);
      }
    });
  };

  const renderFooter = () => {
    if (isLoading) {
      return (
        <button type="button" className="duo-btn duo-btn--primary" disabled>
          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
          Chargement...
        </button>
      );
    }

    return (
      <>
        {!isRole13 && !isTermine && (
          <button type="button" className="duo-btn duo-btn--primary" onClick={handleConfirmReception}>
            <i className="bi bi-check-lg" /> Confirmer
          </button>
        )}
        {!isRole13 && (
          <button type="button" className="duo-btn duo-btn--danger" onClick={handleDeleteProvision}>
            <i className="bi bi-trash" /> Supprimer
          </button>
        )}
        <button type="button" className="duo-btn duo-btn--ghost" onClick={closeModal}>
          Fermer
        </button>
      </>
    );
  };

  const renderDetails = () => (
    <div className="duo-detail-list">
      <DetailRow label="Produit" value={provision?.produit?.name} />
      <DetailRow label="Référence" value={provision?.produit?.reference} />
      <DetailRow label="Statut" value={<span className={statusBadgeClass(provision.status)}>{statusLabel(provision.status)}</span>} />
      <DetailRow label="Quantité" value={provision?.quantity != null ? `${provision.quantity} unités` : null} />
      <DetailRow label="Pièces / carton" value={provision?.nb_pieces_par_carton} />
      <DetailRow label="Cartons total" value={provision?.nb_cartons_total} />
      <DetailRow label="m² / carton" value={provision?.nb_m2_par_carton} />
      <DetailRow label="m² / pièce" value={provision?.nb_m2_par_piece} />
      <DetailRow label="m² total" value={provision?.apro_m2_total} />
      <DetailRow
        label="Prix unitaire revient"
        value={provision?.prix_unitaire_revient != null ? `${formatNumber(provision.prix_unitaire_revient)} FCFA` : null}
      />
      <DetailRow
        label="Prix de vente"
        value={provision?.prix_vente != null ? `${formatNumber(provision.prix_vente)} FCFA` : null}
      />
      <DetailRow
        label="Total achat"
        value={provision?.total_prix_achat != null ? `${formatNumber(provision.total_prix_achat)} FCFA` : null}
      />
      <DetailRow label="Fournisseur" value={provision?.fournisseur?.nom} />
      <DetailRow label="Entrepôt" value={provision?.warehouse?.name} />
      <DetailRow label="Date" value={provision?.created_at ? formatDateWithTime(provision.created_at) : null} />
    </div>
  );

  return (
    <>
      <button
        type="button"
        title="Réception / modifier"
        className="duo-btn duo-btn--ghost duo-btn--sm"
        onClick={() => setIsOpen(true)}
      >
        <i className="bi bi-pencil-square" />
      </button>

      {isOpen && (
        <DuoModal
          size="lg"
          title={isTermine ? 'Réception' : 'Confirmer la réception'}
          onClose={closeModal}
          footer={renderFooter()}
        >
          <div className="duo-modal__hero">
            <i
              className={`bi ${isTermine ? 'bi-check-circle-fill' : 'bi-truck'} duo-modal__hero-icon${isTermine ? '' : ' duo-modal__hero-icon--info'}`}
              aria-hidden
            />
            <p className="duo-modal__hero-text">
              {isTermine
                ? 'Cet approvisionnement est déjà livré.'
                : 'Voulez-vous confirmer la réception de cet approvisionnement ?'}
            </p>
          </div>

          <h3 className="duo-section-title">Détails de l&apos;approvisionnement</h3>
          {renderDetails()}
        </DuoModal>
      )}
    </>
  );
}
